// src/pages/income/cafe/GeumnaraeIncomeCreate.jsx
import React, { useState, useEffect, useMemo } from "react";
import styles from "./GasanIncomeCreate.module.css"; // ✅ 기존 CSS 재사용
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";

export default function GeumnaraeIncomeCreate({ fixedCategory = "CAFE_INN 금나래점" }) {
  const [category] = useState(fixedCategory);
  const [subcategory, setSubcategory] = useState("매출액");

  // ✅ 작성일
  const [draftDate, setDraftDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });

  const [salesDate, setSalesDate] = useState("");
  const [workMonth, setWorkMonth] = useState("");
  const [seq, setSeq] = useState("001");
  const [loadingSeq, setLoadingSeq] = useState(false);
  const [saving, setSaving] = useState(false);

  // ✅ 월별 데이터 (현황표용)
  const [calendarData, setCalendarData] = useState({});

  const [payments, setPayments] = useState({
    card: 0,
    cash_onsite: 0,
    cash_transfer: 0,
    cash_receipt: 0,
    kakaopay: 0,
    zeropay: 0,
    seoulpay: 0,
    tosspay: 0,  
    tmoney: 0,
    coupang: 0,         // ← 추가
    coupang_receipt: 0, // ← 추가
    baemin: 0,
    baemin_receipt: 0,  // ← 추가
  });

  // ✅ 총금액 계산 (현금영수증 제외)
  const totalAmount = useMemo(() => {
    const keysToInclude = [
      "card",
      "cash_onsite",
      "cash_transfer",
      "kakaopay",
      "zeropay",
      "seoulpay",
      "tosspay",  
      "tmoney",  
      
      "coupang",         // ← 추가
      "coupang_receipt", // ← 추가
      "baemin",
      "baemin_receipt",  // ← 추가
    ];

    return keysToInclude
      .map((key) => Number(String(payments[key] || "0").replace(/,/g, "")))
      .reduce((a, b) => a + (isNaN(b) ? 0 : b), 0);
  }, [payments]);

  // ✅ 매출일 형식 변환
  const formattedSalesDate = useMemo(() => {
    if (!salesDate) return "";
    const d = new Date(salesDate);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}년 ${m}월 ${day}일`;
  }, [salesDate]);

  // ✅ 사업구분에서 “금나래점”만 추출
  const storeName = useMemo(() => {
    const parts = category.split(" ");
    return parts[parts.length - 1];
  }, [category]);

  // ✅ 제목 자동완성
  const title = useMemo(() => {
    if (!salesDate || !workMonth)
      return "매출일과 귀속월을 입력하면 자동완성됩니다";
    return `${formattedSalesDate} ${storeName} ${subcategory}`;
  }, [formattedSalesDate, storeName, subcategory, salesDate, workMonth]);

  // ✅ 기안번호 자동 생성
  const yymmdd = salesDate ? salesDate.replaceAll("-", "").slice(2) : "";
  const draftNo = useMemo(() => {
    if (!salesDate || !workMonth)
      return "매출일과 귀속월을 입력하면 자동완성됩니다";
    return `금천일자리(주)카페금나래-${yymmdd}-${seq}호`;
  }, [yymmdd, seq, salesDate, workMonth]);

  // ✅ seq 자동 생성
  // ✅ keep-alive 탭 재방문 시 다음 번호를 다시 조회(마운트가 유지돼 처음 조회값에 멈춰 있지 않게)
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    if (!salesDate) return;
    let ignore = false;
    async function getSeq() {
      setLoadingSeq(true);
      try {
        const safeCategory = encodeURIComponent(category);
        const { data } = await api.get(
          `/income-drafts/next-seq/?category=${safeCategory}&date=${salesDate}`
        );
        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (err) {
        console.error("[next-seq] 실패:", err);
      } finally {
        if (!ignore) setLoadingSeq(false);
      }
    }
    getSeq();
    return () => {
      ignore = true;
    };
  }, [category, salesDate, seqRefreshTick]);

  // ✅ 결제 금액 입력
  // ✅ 결제 취소·환불 등 차감 건 기록용 — 맨 앞의 "-"는 부호로 보존하고 나머지
  // 비숫자만 걸러낸다(기존엔 [^\d] 필터가 "-"까지 지워버려 음수 입력이 불가능했음)
  const handlePayChange = (key, value) => {
    const isNegative = value.trim().startsWith("-");
    const digits = value.replace(/[^\d]/g, "");
    const formattedDigits = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    setPayments((prev) => ({ ...prev, [key]: (isNegative ? "-" : "") + formattedDigits }));
  };

  // ✅ 저장
  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    const payload = {
      category,
      subcategory,
      draft_no: draftNo,
      draft_date: draftDate,
      sales_date: salesDate,
      work_month: workMonth,
      payments,
      amount: totalAmount,
    };

    try {
      // ✅ 금나래점 DB 저장 경로
      await api.post("/income/cafe/geumnarae/", payload);
      alert(`✅ 저장 완료!\n총금액: ${totalAmount.toLocaleString()}원`);

      // 저장 후 현황 갱신
      if (workMonth) {
        const [year, month] = workMonth.split("-");
        fetchCalendarData(year, month);
      }
    } catch (err) {
      console.error("❌ 저장 실패:", err);
      alert("저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ 일자별 현황 데이터 가져오기
  const fetchCalendarData = async (year, month) => {
    try {
      const { data } = await api.get(
        `/income/cafe/geumnarae/monthly-summary/?year=${year}&month=${Number(month)}`
      );
      const normalizedData = Object.entries(data).reduce((acc, [key, value]) => {
        acc[key] = Number(value);
        return acc;
      }, {});
      setCalendarData(normalizedData);
    } catch (err) {
      console.error("❌ 현황 데이터 불러오기 실패:", err);
    }
  };

  useEffect(() => {
    if (!workMonth) return;
    const [year, month] = workMonth.split("-");
    fetchCalendarData(year, month);
  }, [workMonth]);

  // ✅ 월별 현황표 렌더링
  const renderSummaryList = () => {
    if (!workMonth) return <div>먼저 귀속월을 선택하세요.</div>;

    const [year, month] = workMonth.split("-");
    const daysInMonth = new Date(year, month, 0).getDate();
    const weekdays = ["일", "월", "화", "수", "목", "금", "토"];

    const totalMonthAmount = Object.values(calendarData)
      .map((v) => Number(v) || 0)
      .reduce((a, b) => a + b, 0);

    return (
      <div className={styles.summaryBox}>
        <h4>{workMonth} 수입 입력 현황</h4>

        <div
          style={{
            fontWeight: "bold",
            color: "#166534",
            fontSize: "16px",
            marginBottom: "8px",
          }}
        >
          총매출액: {totalMonthAmount.toLocaleString()}원
        </div>

        <ul style={{ listStyle: "none", padding: 0 }}>
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = String(i + 1).padStart(2, "0");
            const key = `${year}-${month}-${day}`;
            const value = calendarData[key] || 0;
            const isFilled = value > 0;
            const dateObj = new Date(`${year}-${month}-${day}`);
            const weekday = weekdays[dateObj.getDay()];

            return (
              <li
                key={key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: isFilled ? "#e6ffed" : "#f9fafb",
                  color: isFilled ? "green" : "#999",
                  fontWeight: isFilled ? "bold" : "normal",
                  borderRadius: "6px",
                  padding: "4px 10px",
                  marginBottom: "3px",
                }}
              >
                <span>
                  {key} ({weekday})
                </span>
                <span>
                  {isFilled
                    ? `입력완료 (${value.toLocaleString()}원)`
                    : "미입력"}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.threePanel}>
        {/* ① 왼쪽 입력 폼 */}
        <div className={styles.leftPanel}>
          <h3>CAFE_INN 금나래점 수입 건 작성</h3>

          <Row label="기안번호(자동완성)">
            <input
              value={draftNo}
              readOnly
              className={!salesDate || !workMonth ? styles.placeholderText : ""}
            />
          </Row>

          <Row label="제목(자동완성)">
            <input
              value={title}
              readOnly
              className={!salesDate || !workMonth ? styles.placeholderText : ""}
            />
          </Row>

          <Row label="사업구분">
            <input value={category} readOnly />
          </Row>

          <Row label="세부구분">
            <input
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
            />
          </Row>

          <Row label="귀속월">
            <input
              type="month"
              value={workMonth}
              onChange={(e) => setWorkMonth(e.target.value)}
            />
          </Row>

          <Row label="작성일">
            <input
              type="date"
              value={draftDate}
              onChange={(e) => setDraftDate(e.target.value)}
            />
          </Row>

          <Row label="매출일">
            <input
              type="date"
              value={salesDate}
              onChange={(e) => setSalesDate(e.target.value)}
            />
          </Row>
        </div>

        {/* ✅ 결제표 + 저장 */}
        <div className={styles.rightPanel}>
          <div className={styles.payTableContainer}>
            <h4>결제방법별 금액 입력</h4>
            <table className={styles.payTable}>
              <thead>
                <tr>
                  <th rowSpan="2">카드결제</th>
                  <th colSpan="3">현금결제</th>
                  <th colSpan="4">페이</th>
                  <th rowSpan="2">티머니</th>
                  <th colSpan="2">쿠팡이츠</th>
                  <th colSpan="2">배달의민족</th>
                </tr>
                <tr>
                  <th>현장결제</th>
                  <th>계좌이체</th>
                  <th>현금영수증</th>
                  <th>카카오</th>
                  <th>제로</th>
                  <th>서울</th>
                  <th>토스</th>
                  <th>매출액</th>
                  <th>현금영수증</th>
                  <th>매출액</th>
                  <th>현금영수증</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <input
                      type="text"
                      value={payments.card}
                      onChange={(e) => handlePayChange("card", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.cash_onsite}
                      onChange={(e) => handlePayChange("cash_onsite", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.cash_transfer}
                      onChange={(e) => handlePayChange("cash_transfer", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.cash_receipt}
                      onChange={(e) => handlePayChange("cash_receipt", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.kakaopay}
                      onChange={(e) => handlePayChange("kakaopay", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.zeropay}
                      onChange={(e) => handlePayChange("zeropay", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.seoulpay}
                      onChange={(e) => handlePayChange("seoulpay", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.tosspay}
                      onChange={(e) => handlePayChange("tosspay", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.tmoney}
                      onChange={(e) => handlePayChange("tmoney", e.target.value)}
                    />
                  </td> <td>
                    <input
                      type="text"
                      value={payments.coupang}
                      onChange={(e) => handlePayChange("coupang", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.coupang_receipt}
                      onChange={(e) => handlePayChange("coupang_receipt", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.baemin}
                      onChange={(e) => handlePayChange("baemin", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={payments.baemin_receipt}
                      onChange={(e) => handlePayChange("baemin_receipt", e.target.value)}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <div className={styles.totalBox}>
              총금액: <b>{totalAmount.toLocaleString()} 원</b>
            </div>
          </div>

          <div className={styles.saveBox}>
            <button onClick={onSubmit} disabled={saving}>
              {saving ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>

        {/* ③ 오른쪽 현황표 */}
        <div className={styles.statusPanel}>{renderSummaryList()}</div>
      </div>
    </div>
  );
}

// ✅ 공통 Row
function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}
